// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  status: number;
}

// Event Types
export interface Event {
  eventKey: string;
  description: string;
  imageUrl: string;
  status: string;
  scheduledStartDatetime: string | null;
  scheduledEndDatetime: string | null;
  eventDetails: string | null;
  sportCategory: string;
}

// Channel Types
export enum StageStatus {
  ACTIVE = "ACTIVE",
  CREATED = "CREATED",
}

export interface ChannelAttributes {
    eventKey: string;
    groupId: string;
    channelArn: string;
    channelUrl: string;
    status: StageStatus;
    liveViewerCount: number;
    chatRoomArn: string;
    user: PublicUserInfo;
    coCommentators: PublicUserInfo[];
}

// Stage Creation Types
export interface CreateStageRequest {
  eventKey: string;
}

export interface CreateStageResponse {
  chatToken: string;
  stageToken: string;
  chatArn: string;
  stageArn: string;
  groupId: string;
  isHost: boolean;
}

// Chat Token Types
export interface ChatTokenRequest {
  chatArn: string;
  userId: string;
  sessionDurationInMinutes: number;
  capabilities: string[];
  attributes?: Record<string, string>;
}

// Simplified chat token request for StreamChat component
export interface StreamChatTokenRequest {
  chatRoomArn: string;
  isHost: boolean;
  attributes?: Record<string, string>; // Add user attributes
}

export interface ChatTokenResponse {
  sessionExpirationTime: string;
  token: string;
  tokenExpirationTime: string;
}

// Chat token interface for AWS IVS Chat SDK
export interface ChatToken {
  token: string;
  sessionExpirationTime: Date;
  tokenExpirationTime: Date;
}

// User Types
export interface UserAttributes {
    userId: string;
    publicHandle: string;
    displayName: string;
    avatarUrl: string;
    preferredLanguage: string;
    description: string;
    nationality: string;
    joinedTimestamp: Date;
    marketingConsent: boolean;
    leaderboardConsent: boolean;
    onboarded: boolean;
    termsAndConditionsConsent: Date | null;
    userSuspended: Date | null;
    yearOfBirth: number | null;
    ageVerifiedAt: Date | null;
}

export interface PublicUserInfo {
    publicHandle: string;
    displayName: string;
    avatarUrl: string;
    preferredLanguage: string;
    description: string;
    nationality: string;
    joinedTimestamp: Date;
}

// Invite Types
export interface SendCoCommentatorInviteRequest {
    groupId: string;
    inviteePublicHandle: string;
}

export interface RemoveCoCommentatorInviteRequest {
    groupId: string;
    inviteePublicHandle: string;
}

// Report Content Types
export interface ReportContentRequest {
    groupId: string;
    category: string;
    details?: string;
}

// Content Reports Types
export interface ContentReport {
    id: number;
    category: string;
    details: string | null;
    createdAt: string;
    reporter: {
        userId: string;
        displayName: string;
        avatarUrl: string;
        publicHandle: string;
    };
    group: {
        groupId: string;
        isHost: boolean;
    };
    stage: {
        stageArn: string;
        channelArn: string;
        channelUrl: string;
        status: string;
    };
    event: {
        eventKey: string;
        description: string;
        imageUrl: string;
        status: string;
        scheduledStartDatetime: string | null;
    };
    reportedUser: {
        userId: string;
        displayName: string;
        avatarUrl: string;
        publicHandle: string;
    };
}

export interface ContentReportsResponse {
    reports: ContentReport[];
    total: number;
}

export interface ContentReportsFilters {
    limit?: number;
    offset?: number;
    category?: string;
    eventKey?: string;
}

export interface UserUpdateRequest {
  displayName?: string;
  preferredLanguage?: string;
  nationality?: string;
  description?: string;
}

export interface UserProfilePicturePresignURLRequest {
  fileName: string;
  contentType: string;
}

export interface UserProfilePicturePresignURLResponse {
  message: string;
  fileUrl: string;
  uploadUrl: string;
}

// Feedback Types
export interface FeedbackItem {
  id: number;
  type: 'bug' | 'improvement';
  description: string;
  url: string;
  userAgent: string;
  screenshotKey?: string;
  createdAt: string;
}

export interface FeedbackStats {
  totalCount: number;
  bugCount: number;
  improvementCount: number;
  percentages: {
    bugs: number;
    improvements: number;
  };
  dailyStats: Array<{
    date: string;
    total: number;
    bugs: number;
    improvements: number;
  }>;
  topUrls: Array<{
    url: string;
    count: number;
  }>;
}

export interface FeedbackFilters {
  type?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
}

export interface FeedbackListResponse {
  feedback: FeedbackItem[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Restreamer Types
export interface ProcessFilters {
  filter?: string;
  reference?: string;
  id?: string;
  idpattern?: string;
  refpattern?: string;
}

export interface Process {
  id: string;
  type: string;
  reference?: string;
  created_at: number;
  updated_at: number;
  config: ProcessConfig;
  state?: ProcessState;
  report?: ProcessReport;
  metadata?: any;
}

export interface ProcessConfig {
  id?: string;
  type: 'ffmpeg' | '';
  reference?: string;
  input: ProcessIO[];
  output: ProcessIO[];
  options?: string[];
  autostart?: boolean;
  reconnect?: boolean;
  reconnect_delay_seconds?: number;
  stale_timeout_seconds?: number;
  limits?: ProcessLimits;
}

export interface ProcessIO {
  id?: string;
  address: string;
  options?: string[];
  cleanup?: ProcessCleanup[];
}

export interface ProcessCleanup {
  pattern: string;
  max_files?: number;
  max_file_age_seconds?: number;
  purge_on_delete?: boolean;
}

export interface ProcessLimits {
  cpu_usage?: number;
  memory_mbytes?: number;
  waitfor_seconds?: number;
}

export interface ProcessState {
  exec: string;
  order: string;
  command: string[];
  progress?: ProcessProgress;
  cpu_usage: number;
  memory_bytes: number;
  runtime_seconds: number;
  reconnect_seconds?: number;
  last_logline?: string;
}

export interface ProcessProgress {
  frame: number;
  packet: number;
  fps: number;
  q: number;
  size_kb: number;
  time: number;
  bitrate_kbit: number;
  speed: number;
  drop: number;
  dup: number;
  inputs?: ProcessProgressIO[];
  outputs?: ProcessProgressIO[];
}

export interface ProcessProgressIO {
  index: number;
  stream: number;
  id?: string;
  address?: string;
  type: string;
  codec: string;
  coder: string;
  format: string;
  frame: number;
  packet: number;
  fps: number;
  q: number;
  size_kb: number;
  bitrate_kbit: number;
  pps: number;
  keyframe: number;
  width?: number;
  height?: number;
  pix_fmt?: string;
  channels?: number;
  layout?: string;
  sampling_hz?: number;
}

export interface ProcessReport {
  created_at: number;
  prelude: string[];
  log: string[][];
  history?: ProcessReportHistoryEntry[];
}

export interface ProcessReportHistoryEntry {
  created_at: number;
  prelude: string[];
  log: string[][];
}

// User Search Types
export interface UserSearchRequest {
    displayName?: string;
    page?: number;
    limit?: number;
}

export interface UserSearchResponse {
    users: PublicUserInfo[];
    total: number;
    page: number;
    limit: number;
}

// Stripe Types
export interface StripeClientSecretResponse {
  client_secret: string;
}

export type StripeSessionResponse = {
  session_id: string;
  status: string;
  payment_status: string;
  customer_email: string | null;
  amount_total: number | null;
  currency: string | null;
  created: number;
  expires_at: number;
  metadata: Record<string, string>;
  customer_details: any;
  payment_intent: string | null;
};
